import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ErrorPanel, LoadingRow, EmptyNote, TableWrap, Th, Td, Pager, Segmented, MarketTag } from '../components/ui/bits';
import { Card } from '../components/ui/Card';
import { useRpc } from '../lib/queries';
import { agoLabel, fmtDate } from '../lib/format';
import { classMeta, CLASS_META } from '../lib/classes';
import { selfLabel, channelLabel, planLabel } from '../lib/labels';
import type { Paged, UserRow } from '../lib/types';

const PER = 50;

/** Every account, internal ones excluded. Counted and paged in SQL (admin_dash_users). */
export const UsersPage: React.FC = () => {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const cls = params.get('class') ?? 'all';
  const market = params.get('market') ?? 'all';
  const payer = params.get('payer') ?? 'all';
  const sort = params.get('sort') ?? 'joined';
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);

  const { data, error, isLoading, isFetching } = useRpc<Paged<UserRow>>('admin_dash_users', {
    p_search: q || null, p_class: cls === 'all' ? null : cls, p_market: market === 'all' ? null : market,
    p_payer: payer === 'all' ? null : payer, p_sort: sort, p_page: page, p_per: PER,
  });

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v && v !== 'all') next.set(k, v); else next.delete(k);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const rows = data?.rows ?? [];

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={<>
          {data ? `${data.total.toLocaleString('en-GB')} accounts match.` : 'Loading.'} Internal and test accounts are excluded.
          Last active is the latest session, psychometric run, Vyo message, tool or in-app action, not the last sign-in.
        </>}
      />

      <Card padding="sm" className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={e => { e.preventDefault(); set({ q: draft }); }} className="flex items-center gap-2">
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Email, name, university or user id"
              aria-label="Search users"
              className="h-8 w-72 px-3 rounded-md border border-stone-200 bg-white focus:border-indigo-400 focus:outline-none text-[13px]"
            />
            <Button type="submit" size="sm" variant="primary">Search</Button>
          </form>
          <Segmented label="Market" value={market} onChange={v => set({ market: v })}
            options={[{ value: 'all', label: 'UK and US' }, { value: 'uk', label: 'UK' }, { value: 'us', label: 'US' }]} />
          <Segmented label="Payer" value={payer} onChange={v => set({ payer: v })}
            options={[{ value: 'all', label: 'Everyone' }, { value: 'payer', label: 'Paying now' }, { value: 'ever_paid', label: 'Ever paid' }, { value: 'never_paid', label: 'Never paid' }]} />
          <select
            value={cls}
            onChange={e => set({ class: e.target.value })}
            aria-label="Subscription class"
            className="h-8 px-2 rounded-md border border-stone-200 bg-white text-[13px] text-stone-700"
          >
            <option value="all">Any subscription state</option>
            {Object.entries(CLASS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <Segmented label="Sort" value={sort} onChange={v => set({ sort: v })}
            options={[{ value: 'joined', label: 'Newest' }, { value: 'active', label: 'Recently active' }]} />
          {isFetching && !isLoading && <span className="text-[12px] text-stone-400">Updating</span>}
        </div>
      </Card>

      {error && <ErrorPanel error={error} label="admin_dash_users" />}
      {isLoading && <LoadingRow label="Loading users" />}

      {data && (
        <Card>
          {rows.length === 0 ? <EmptyNote>No users match</EmptyNote> : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>User</Th><Th>Market</Th><Th>Access</Th><Th>Paid</Th><Th>Heard about us</Th><Th>First touch</Th>
                  <Th>Joined</Th><Th>Last active</Th><Th>Flags</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map(u => {
                  const meta = classMeta(u.class);
                  return (
                    <tr key={u.id} onClick={() => nav(`/users/${u.id}`)} className="cursor-pointer hover:bg-stone-50">
                      <Td>
                        <div className="font-medium text-stone-900">{u.full_name || u.email || u.id.slice(0, 8)}</div>
                        <div className="text-[12px] text-stone-500">{u.full_name ? u.email : ''}{u.university ? `${u.full_name ? ' · ' : ''}${u.university}` : ''}</div>
                      </Td>
                      <Td><MarketTag market={u.market} /></Td>
                      <Td>
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                        {u.plan && u.plan !== 'free' && <div className="text-[12px] text-stone-500 mt-0.5">{planLabel(u.plan)}</div>}
                      </Td>
                      <Td>
                        {u.payer ? <Badge tone="emerald">Paying now</Badge> : u.ever_paid ? <Badge tone="stone">Paid before</Badge> : <span className="text-stone-400">Never</span>}
                        {(u.paid_packs > 0 || u.comped_packs > 0) && (
                          <div className="text-[12px] text-stone-500 mt-0.5">
                            {u.paid_packs > 0 && `${u.paid_packs} paid pack${u.paid_packs > 1 ? 's' : ''}`}
                            {u.paid_packs > 0 && u.comped_packs > 0 && ', '}
                            {u.comped_packs > 0 && `${u.comped_packs} comped`}
                          </div>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap">{selfLabel(u.self_source)}</Td>
                      <Td className="whitespace-nowrap text-stone-500">{channelLabel(u.channel)}</Td>
                      <Td className="whitespace-nowrap" ><span title={fmtDate(u.created_at)}>{agoLabel(u.created_at)}</span></Td>
                      <Td className="whitespace-nowrap">
                        <span title={u.last_sign_in_at ? `Last sign-in ${fmtDate(u.last_sign_in_at)}` : 'Never signed in'}>
                          {u.last_active ? agoLabel(u.last_active) : <span className="text-stone-400">No activity</span>}
                        </span>
                      </Td>
                      <Td>
                        <span className="flex flex-wrap gap-1">
                          {u.deletion_pending && <Badge tone="rose">Deletion requested</Badge>}
                          {!u.onboarded && <Badge tone="stone">Not onboarded</Badge>}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
          )}
          <Pager page={page} per={PER} total={data.total} onPage={p => set({ page: String(p) })} />
          <p className="text-[11px] text-stone-400 mt-3">
            Source: admin_dash_users. Paying now = Stripe-linked subscription that is paying, cancelling or past due, or an unexpired paid pack.
            Ever paid = any Stripe subscription id or any paid pack. Comps never count as paid.
          </p>
        </Card>
      )}
    </div>
  );
};
